import { css } from "@emotion/react";
import type { RowClickedEvent } from "ag-grid-community";
import { useMemo, useState } from "react";
import type { UsersWithPermissions } from "../../../../../api/src/types";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton } from "../../bits/buttons";
import { AdminTable } from "../../bits/inputs/tables/AdminTable";
import { userTableColDef } from "../../bits/inputs/tables/userColDef";
import { FlexRowContainer } from "../../bits/layout";
import { LoadingSpinner } from "../../bits/loading";
import { Modal } from "../../bits/Modal";
import { useTitle } from "../../hooks/useTitle";
import type { UserFormData } from "../../patterns/AddUserForm";
import { AddUserForm } from "../../patterns/AddUserForm";
import { UploadBookAccessForm } from "../../patterns/UploadBookAccessForm";

export const UserAdmin = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { data: publications } = trpc.adminGetPublications.useQuery();
  const [editingUser, setEditingUser] =
    useState<UsersWithPermissions[number]>();
  const { data } = trpc.adminGetUsers.useQuery();
  const createUser = trpc.createUser.useMutation({
    onSuccess: () => setIsModalOpen(false),
  });
  const updateUser = trpc.adminUpdateUser.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      setEditingUser(undefined);
    },
  });
  const deleteUser = trpc.adminDeleteUser.useMutation({
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setIsModalOpen(false);
      setEditingUser(undefined);
    },
  });
  const resendInvite = trpc.adminResendInvite.useMutation();
  useTitle("User Admin | Compass");

  const handleSubmit = (user: UserFormData) => {
    if (editingUser) {
      updateUser.mutate({
        ...editingUser,
        permissions: editingUser.permissions?.map((p) => p.id),
        ...user,
        expiry: user.expiry.toISOString(),
        organisationId: user.organisationId ?? null,
      });
    } else {
      createUser.mutate({
        ...user,
        expiry: user.expiry.toISOString(),
        organisationId: user.organisationId ?? undefined,
      });
    }
  };

  const handleSelectRow = (event: RowClickedEvent) => {
    setEditingUser(event.data);
    setIsModalOpen(true);
  };

  const adminPermissions = useMemo(
    () =>
      publications &&
      publications.reduce((acc, pub) => {
        return {
          ...acc,
          ["publication-" + pub.id]: true,
        };
      }, {}),
    [publications],
  );

  const ssoStatusToCheck =
    editingUser?.organisation?.ssoStatus ?? editingUser?.ssoStatus;

  const userDataWithPermissions = useMemo(() => {
    if (!data) return [];

    return data.map((user) => {
      const userPermissions = user.organisation
        ? user.organisation.permissions
        : user.permissions;

      const permissions = userPermissions.reduce((acc, pub) => {
        return {
          ...acc,
          ["publication-" + pub.publicationId]: true,
        };
      }, {});
      return {
        ...user,
        ...permissions,
        ...(user.admin ? adminPermissions : {}),
      };
    });
  }, [adminPermissions, data]);

  const userTableColDefWithPublications = useMemo(() => {
    const colDef = userTableColDef(publications);
    return colDef;
  }, [publications]);

  return (
    <div style={{ padding: "1rem" }}>
      <div
        css={css`
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          width: 100%;
        `}
      >
        <Modal
          key="Upload Book Access Modal"
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setEditingUser(undefined);
          }}
          renderButton={(props) => (
            <FlexRowContainer style={{ justifyContent: "end" }}>
              <PrimaryButton
                {...props}
                onClick={() => setIsUploadModalOpen(true)}
                disabled={createUser.isLoading}
                style={{ marginBottom: "1rem" }}
              >
                Upload Book Access
              </PrimaryButton>
            </FlexRowContainer>
          )}
          header={<h2>Upload Book Access</h2>}
          content={
            <UploadBookAccessForm
              onSubmit={() => {
                setIsUploadModalOpen(false);
              }}
            />
          }
        />
        <Modal
          key="modal"
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingUser(undefined);
            createUser.reset();
            updateUser.reset();
            deleteUser.reset();
            resendInvite.reset();
          }}
          renderButton={(props) => (
            <FlexRowContainer style={{ justifyContent: "end" }}>
              <PrimaryButton
                {...props}
                onClick={() => setIsModalOpen(true)}
                disabled={createUser.isLoading}
                style={{ marginBottom: "1rem" }}
              >
                Add new user
              </PrimaryButton>
            </FlexRowContainer>
          )}
          header={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
                width: "100%",
              }}
            >
              <h2>{editingUser ? "Update user" : "Add new user"}</h2>
              {editingUser && (
                <FlexRowContainer style={{ gap: "8px", alignItems: "center" }}>
                  {editingUser.cognitoStatus === "FORCE_CHANGE_PASSWORD" &&
                    (ssoStatusToCheck === "password_only" ||
                      ssoStatusToCheck === "all") && (
                      <PrimaryButton
                        onClick={() =>
                          resendInvite.mutate({ email: editingUser.email })
                        }
                        disabled={
                          resendInvite.isLoading || resendInvite.isSuccess
                        }
                        style={{ marginBottom: "1rem" }}
                      >
                        {resendInvite.isSuccess
                          ? "Invite Sent"
                          : "Reset Temp Password"}
                      </PrimaryButton>
                    )}
                  <Modal
                    key="deleteModal"
                    isOpen={isDeleteModalOpen}
                    onClose={() => {
                      setIsDeleteModalOpen(false);
                    }}
                    renderButton={() => (
                      <FlexRowContainer style={{ justifyContent: "end" }}>
                        <PrimaryButton
                          onClick={() => setIsDeleteModalOpen(true)}
                          disabled={deleteUser.isLoading}
                          style={{ marginBottom: "1rem" }}
                        >
                          Delete User
                        </PrimaryButton>
                      </FlexRowContainer>
                    )}
                    header={<h2>Delete User</h2>}
                    content={
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          gap: 8px;
                        `}
                      >
                        {`Are you sure you want to delete ${editingUser.name}, ${editingUser.email}?`}
                      </div>
                    }
                    footer={
                      <PrimaryButton
                        onClick={() => {
                          deleteUser.mutate(editingUser);
                        }}
                      >
                        Delete
                      </PrimaryButton>
                    }
                  />
                </FlexRowContainer>
              )}
            </div>
          }
          content={
            <AddUserForm
              onSubmit={handleSubmit}
              error={
                createUser.error?.message ??
                updateUser.error?.message ??
                deleteUser.error?.message ??
                ""
              }
              user={editingUser}
            />
          }
          footer={<></>}
        />
      </div>
      {data ? (
        <AdminTable
          rowData={userDataWithPermissions}
          columnDefs={userTableColDefWithPublications}
          onRowClicked={handleSelectRow}
        />
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};
